import React, { useRef, useEffect } from 'react';
import { X } from 'lucide-react';

interface PrivacyPolicyProps {
  isOpen: boolean;
  onClose: () => void;
}

function PrivacyPolicy({ isOpen, onClose }: PrivacyPolicyProps) {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    }

    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose();
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        ref={modalRef}
        className="bg-slate-900/90 backdrop-blur-xl border border-slate-700/50 rounded-xl p-6 max-w-2xl w-full relative max-h-[80vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute -right-2 -top-2 w-12 h-12 flex items-center justify-center rounded-xl hover:bg-slate-800/50 text-slate-400 hover:text-slate-200 transition-all duration-300 backdrop-blur-sm"
          aria-label="Close privacy policy"
        >
          <div className="bg-slate-900/90 w-8 h-8 rounded-lg flex items-center justify-center">
            <X className="w-5 h-5" />
          </div>
        </button>

        <div className="mb-6">
          <h1 className="text-2xl font-display font-bold bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400 bg-clip-text text-transparent">
            Privacy Policy
          </h1>
          <p className="text-slate-400 mt-2">Last updated: February 20, 2025</p>
        </div>

        <div className="overflow-y-auto pr-2 custom-scrollbar">
          <div className="space-y-6 text-slate-300">
            <section className="glass-panel p-4 rounded-xl">
              <h2 className="text-lg font-display font-bold text-slate-200 mb-3">1. Introduction</h2>
              <p className="leading-relaxed">
                Ask Rumi ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and safeguard your information when you use our educational AI platform.
              </p>
            </section>

            <section className="glass-panel p-4 rounded-xl">
              <h2 className="text-lg font-display font-bold text-slate-200 mb-3">2. Information We Collect</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="font-medium text-slate-200">2.1 Information you provide:</h3>
                  <ul className="list-disc pl-6 space-y-2 mt-2">
                    <li>Email address and account credentials</li>
                    <li>Profile information (display name, grade level, interests)</li>
                    <li>Questions and conversations with our AI</li>
                    <li>Feedback and ratings</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-medium text-slate-200">2.2 Automatically collected information:</h3>
                  <ul className="list-disc pl-6 space-y-2 mt-2">
                    <li>Device information and browser type</li>
                    <li>Usage patterns and interaction data</li>
                    <li>Performance metrics and error data</li>
                  </ul>
                </div>
              </div>
            </section>

            <section className="glass-panel p-4 rounded-xl">
              <h2 className="text-lg font-display font-bold text-slate-200 mb-3">3. How We Use Your Information</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>To provide and personalize our educational services</li>
                <li>To improve our AI responses and user experience</li>
                <li>To maintain and enhance platform security</li>
                <li>To communicate important updates and announcements</li>
                <li>To analyze usage patterns and optimize performance</li>
              </ul>
            </section>

            <section className="glass-panel p-4 rounded-xl">
              <h2 className="text-lg font-display font-bold text-slate-200 mb-3">4. Data Protection</h2>
              <p className="leading-relaxed">
                We implement industry-standard security measures to protect your data, including:
              </p>
              <ul className="list-disc pl-6 mt-3 space-y-2">
                <li>Encryption of data in transit and at rest</li>
                <li>Regular security audits and updates</li>
                <li>Strict access controls and authentication</li>
                <li>Secure database management through Supabase</li>
              </ul>
            </section>

            <section className="glass-panel p-4 rounded-xl">
              <h2 className="text-lg font-display font-bold text-slate-200 mb-3">5. Data Sharing</h2>
              <p className="leading-relaxed">
                We do not sell your personal information. We may share data with:
              </p>
              <ul className="list-disc pl-6 mt-3 space-y-2">
                <li>Service providers who assist in platform operations</li>
                <li>Analytics services to improve user experience</li>
                <li>Law enforcement when required by law</li>
              </ul>
            </section>

            <section className="glass-panel p-4 rounded-xl">
              <h2 className="text-lg font-display font-bold text-slate-200 mb-3">6. Your Rights</h2>
              <p className="leading-relaxed">
                You have the right to:
              </p>
              <ul className="list-disc pl-6 mt-3 space-y-2">
                <li>Access your personal data</li>
                <li>Correct inaccurate information</li>
                <li>Request deletion of your data</li>
                <li>Export your data in a portable format</li>
                <li>Opt-out of non-essential data collection</li>
              </ul>
            </section>

            <section className="glass-panel p-4 rounded-xl">
              <h2 className="text-lg font-display font-bold text-slate-200 mb-3">7. Contact Us</h2>
              <p className="leading-relaxed">
                If you have any questions about this Privacy Policy or our data practices, please contact us at:
              </p>
              <p className="mt-3 text-blue-400">rumiaiengine@gmail.com</p>
            </section>
          </div>
        </div>
      </div>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
        }

        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(15, 23, 42, 0.3);
          border-radius: 4px;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: linear-gradient(to bottom, rgba(59, 130, 246, 0.5), rgba(147, 51, 234, 0.5));
          border-radius: 4px;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(to bottom, rgba(59, 130, 246, 0.7), rgba(147, 51, 234, 0.7));
        }
      `}</style>
    </div>
  );
}

export default PrivacyPolicy;